import "./DSAUploads.scss";
import { Spinner } from "../account/misc/Spinner";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Input, message } from "antd";
import { DownOutlined, FolderOutlined, PlusOutlined, RightOutlined, SearchOutlined, UpOutlined } from "@ant-design/icons";
import { AppContext } from "../../AppContext";
import { locationsState, mobileState } from "../../state/atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import { SearchableUpload } from "../dsa/EditScreenContentModal";
import { DeleteUploadModal } from "../dsa/DeleteUploadModal";
import UploadCard from "../dsa/UploadCard";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import PreviewModal from "../dsa/PreviewModal";
import { DsaPlaylistDto, DsaUploadFolderDto } from "../../generated/dashboard-client";
import DSAApi from "../../api/DSAApi";
import UploadDisplayImage from "../dsa/UploadDisplayImage";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import { DsaFolderButton, DsaFolderThumb } from "../dsa/DsaFolderButton";
import { DsaCreateFolderModal } from "../dsa/DsaCreateFolderModal";
import { DsaCreateFolderDrawer } from "../dsa/DsaCreateFolderDrawer";
import { DsaMoveToFolderModal } from "../dsa/DsaMoveToFolderModal";
import { DsaMoveToFolderDrawer } from "../dsa/DsaMoveToFolderDrawer";
import { DsaDeleteFolderModal } from "../dsa/DsaDeleteFolderModal";
import { getS3Url } from "../../util/utils";

export default function DSAUploads() {
  const { location } = useContext(AppContext);
  const [locations] = useRecoilState(locationsState);
  const mobile = useRecoilValue(mobileState);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploads, setUploads] = useState([] as SearchableUpload[]);
  const [playlists, setPlaylists] = useState([] as DsaPlaylistDto[]);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [selected, setSelected] = useState({} as DisplayUploadDto);
  const [isPreviewModal, setPreviewModal] = useState(false);

  const [folders, setFolders] = useState([] as DsaUploadFolderDto[]);
  const [activeFolder, setActiveFolder] = useState(null as DsaUploadFolderDto | null);
  const [shownFolders, setShownFolders] = useState([] as DsaUploadFolderDto[]);
  const [breadcrumbs, setBreadcrumbs] = useState([] as DsaUploadFolderDto[]);
  const [folderThumbs, setFolderThumbs] = useState([] as DsaFolderThumb[]);
  const [isFoldersExpanded, setIsFoldersExpanded] = useState(true);
  const [folderToMove, setFolderToMove] = useState(null as DsaUploadFolderDto | null);

  const [isCreateFolderModal, setCreateFolderModal] = useState(false);
  const [isMoveToFolderModal, setMoveToFolderModal] = useState(false);
  const [isDeleteFolderModal, setDeleteFolderModal] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null as DisplayUploadDto | null);

  useEffect(() => {
    const newShownFolders = folders.filter(fol => {
      if (!activeFolder && !fol.parentId) return true;
      if (activeFolder && parseInt(fol.parentId!) === activeFolder.id!) return true;
      return false;
    });
    setShownFolders([...newShownFolders]);
  }, [folders, activeFolder]);

  useEffect(() => {
    const newBreadcrumbs = [] as DsaUploadFolderDto[];
    function recurseFolder(folder: DsaUploadFolderDto | undefined) {
      if (folder) {
        let parent: DsaUploadFolderDto | undefined = undefined;
        folders.forEach(fol => {
          if (fol.id! === parseInt(folder.parentId!)) parent = fol;
        });
        if (parent) {
          newBreadcrumbs.unshift(parent);
          recurseFolder(parent);
        }
      }
    }
    if (activeFolder) recurseFolder(activeFolder);
    setBreadcrumbs([...newBreadcrumbs]);
  }, [activeFolder, folders]);

  const refreshFolderThumbs = useCallback((currentFolders: DsaUploadFolderDto[], currentUploads: SearchableUpload[]) => {
    const newThumbs = [] as DsaFolderThumb[];
    currentFolders.forEach(fol => {
      const firstUpload = currentUploads.find(u => u.folderId === fol.id!.toString());
      if (firstUpload) {
        newThumbs.push({ folderId: fol.id ?? 0, thumb: getS3Url(firstUpload.path!) });
      }
    });
    setFolderThumbs([...newThumbs]);
  }, []);

  const ensureFolderCounts = useCallback((currentUploads: SearchableUpload[], currentFolders: DsaUploadFolderDto[]) => {
    const folderCounts: Record<number, number> = {};
    currentFolders.forEach(fol => {
      folderCounts[fol.id!] = 0;
      currentFolders.forEach(childFol => {
        if (childFol.parentId === fol.id?.toString()) {
          folderCounts[fol.id!] = folderCounts[fol.id!] + 1;
        }
      });
    });

    currentUploads.forEach(u => {
      if (u.folderId) {
        const fol = currentFolders.find(f => f.id!.toString() === u.folderId);
        if (fol) {
          folderCounts[fol.id!] = folderCounts[fol.id!] + 1;
        }
      }
    });

    currentFolders.forEach(fol => {
      if (folderCounts[fol.id!] !== fol.count) {
        DSAApi.overrideFolderCount(fol.id!, folderCounts[fol.id!]).then(() => {
          fol.count = folderCounts[fol.id!];
          setFolders([...currentFolders]);
        }).catch(() => {});
      }
    });

    refreshFolderThumbs(currentFolders, currentUploads);
  }, [refreshFolderThumbs]);

  const fetchUploads = useCallback(() => {
    setLoading(true);
    Promise.all([
      QRDisplayUploadsApi.getDisplayUploadsByLocationIds(locations.map(l => l.internalId!)),
      DSAApi.getLocationFolders(location.internalId!),
      DSAApi.getAssignments(location.internalId!)
    ]).then(([uploadsResp, foldersResp, assignmentsResp]) => {
      const dus = uploadsResp.data as SearchableUpload[];
      const assignmentMap: Record<string, string> = {};
      assignmentsResp.data.forEach(a => {
        if (a.uploadId && a.folderId) {
          assignmentMap[a.uploadId] = a.folderId.toString();
        }
      });
      dus.forEach(u => {
        u.folderId = assignmentMap[u.id] ?? undefined;
        u.visible = u.locationId === location.internalId && !u.folderId;
      });

      const alphaSortedFolders = foldersResp.data.sort((a, b) =>
        (a.name ?? "").replace(/\s/g, "").localeCompare((b.name ?? "").replace(/\s/g, ""), "en", { numeric: true })
      );

      setUploads(dus);
      setFolders(alphaSortedFolders);
      ensureFolderCounts(dus, alphaSortedFolders);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlaylists = useCallback(() => {
    DSAApi.getPlaylists().then(r => {
      setPlaylists(r.data);
    });
  }, []);

  useEffect(fetchUploads, [fetchUploads]);
  useEffect(fetchPlaylists, [fetchPlaylists]);

  function onSearchChange(searchVal: string) {
    setSearch(searchVal);
    uploads.forEach(u => {
      if (activeFolder) {
        if (u.folderId !== activeFolder.id!.toString()) {
          u.visible = false;
          return;
        }
      } else {
        if (u.folderId) {
          u.visible = false;
          return;
        }
      }
      u.visible = u.name!.toLowerCase().includes(searchVal.toLowerCase());
    });
    setUploads([...uploads]);
  }

  function onDeleteModalClose(fetch: boolean) {
    const selectedId = selected.id;
    setDeleteModal(false);
    setSelected({} as DisplayUploadDto);
    if (fetch) {
      playlists.forEach(p => {
        const entriesWithUpload = p.entries!.filter(e => e.uploadId !== selectedId);
        if (entriesWithUpload.length > 0) {
          entriesWithUpload.forEach(fe => { fe.sequence = entriesWithUpload.indexOf(fe); });
          p.entries = entriesWithUpload;
          DSAApi.updatePlaylist(p.id!, p).then(() => {
            console.log(`Removed upload ${selectedId} from playlist ${p.id!}`);
          });
        }
      });
      setTimeout(() => { fetchUploads(); }, 100);
    }
  }

  function onFolderClick(folder: DsaUploadFolderDto) {
    if (folder.id === activeFolder?.id) return;
    uploads.forEach(u => { u.visible = u.folderId === folder.id!.toString(); });
    setUploads([...uploads]);
    setActiveFolder(folder);
    setSearch("");
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  function clearFolder() {
    uploads.forEach(u => { u.visible = !u.folderId; });
    setUploads([...uploads]);
    setActiveFolder(null);
    setSearch("");
  }

  function onDeleteFolderClick(folder: DsaUploadFolderDto) {
    setFolderToMove(folder);
    setDeleteFolderModal(true);
  }

  function onMoveFolderToFolderClick(folder: DsaUploadFolderDto) {
    setFolderToMove(folder);
    setMoveToFolderModal(true);
  }

  function onMoveUploadToFolder(upload: DisplayUploadDto) {
    setSelectedUpload(upload);
    setFolderToMove(null);
    setMoveToFolderModal(true);
  }

  function onRemoveUploadFromFolder(upload: DisplayUploadDto) {
    DSAApi.removeUploadsFromFolder([upload.id]).then(() => {
      const found = uploads.find(u => u.id === upload.id);
      if (found) {
        found.folderId = undefined;
        found.visible = false;
      }
      setUploads([...uploads]);
      ensureFolderCounts(uploads, folders);
      message.success("Upload removed from folder");
    });
  }

  function folderNameExists(currentFolder: DsaUploadFolderDto | null, potentialName: string) {
    const foldersToSearch = currentFolder ? folders.filter(f => f.id !== currentFolder.id) : folders;
    if (foldersToSearch.map(f => f.name ?? "").includes(potentialName)) {
      message.error(`You already have a folder named ${potentialName}`);
      return true;
    }
  }

  function afterFolderRename(folder: DsaUploadFolderDto, name: string) {
    folders.forEach(f => { if (f.id === folder.id) f.name = name; });
    setFolders([...folders]);
  }

  function onCreateFolderModalClose(uploadIds: string[], folderToMoveArg?: DsaUploadFolderDto | null, folder?: DsaUploadFolderDto, parentFolder?: DsaUploadFolderDto | null) {
    setCreateFolderModal(false);
    setMoveToFolderModal(false);

    if (folder) {
      folder.count = (folder.count ?? 0) + uploadIds.length;
      if (folderToMoveArg) {
        folders.forEach(f => {
          if (f.id === folderToMoveArg.id) f.parentId = folder.id?.toString();
        });
      }
      if (parentFolder) {
        folder.parentId = parentFolder.id!.toString();
      }
      const newFolders = [folder, ...folders];
      setFolders(newFolders);

      if (uploadIds.length > 0) {
        uploads.forEach(u => {
          if (uploadIds.includes(u.id)) {
            u.folderId = folder.id!.toString();
            u.visible = false;
          }
        });
        setUploads([...uploads]);
        ensureFolderCounts(uploads, newFolders);
      }
    }
    setSelectedUpload(null);
    setFolderToMove(null);
  }

  function onMoveToFolderClose(save: boolean, folder?: DsaUploadFolderDto) {
    setMoveToFolderModal(false);
    if (save && selectedUpload) {
      if (folder) {
        uploads.forEach(u => {
          if (u.id === selectedUpload.id) {
            u.folderId = folder.id!.toString();
            if (!activeFolder || activeFolder.id !== folder.id) u.visible = false;
          }
        });
        setUploads([...uploads]);
        setTimeout(() => { ensureFolderCounts(uploads, folders); }, 250);
        message.success("Upload moved to folder");
      }
    }
    setSelectedUpload(null);
    setFolderToMove(null);

    if (mobile) {
      let drawers = document.querySelectorAll(".ant-drawer-open .ant-drawer-mask");
      drawers.forEach(d => (d as HTMLElement).click());
    }
  }

  function onMoveToUploadsClose(uploadIds: string[]) {
    setMoveToFolderModal(false);
    uploads.forEach(u => {
      if (uploadIds.includes(u.id)) {
        u.folderId = undefined;
        u.visible = !activeFolder;
      }
    });
    setUploads([...uploads]);
    ensureFolderCounts(uploads, folders);
    message.success("Upload moved to All Uploads");
    setSelectedUpload(null);
    setFolderToMove(null);

    if (mobile) {
      let drawers = document.querySelectorAll(".ant-drawer-open .ant-drawer-mask");
      drawers.forEach(d => (d as HTMLElement).click());
    }
  }

  function onMoveToFolderMovedFolderClose(childFolderId: number, parentFolderId: string) {
    if (childFolderId) {
      message.success("Folder moved");
      folders.forEach(f => { if (f.id === childFolderId) f.parentId = parentFolderId; });
      setFolders([...folders]);
      ensureFolderCounts(uploads, [...folders]);
    }
    setMoveToFolderModal(false);
    setFolderToMove(null);

    if (mobile) {
      let drawers = document.querySelectorAll(".ant-drawer-open .ant-drawer-mask");
      drawers.forEach(d => (d as HTMLElement).click());
    }
  }

  function onDeleteFolderSuccess(removedFolderId: number) {
    setDeleteFolderModal(false);
    let newFolders = folders.filter(f => f.id !== removedFolderId);

    const uploadsInFolder = uploads.filter(u => u.folderId === removedFolderId.toString());
    uploadsInFolder.forEach(u => { u.folderId = undefined; });
    if (uploadsInFolder.length > 0) {
      DSAApi.removeUploadsFromFolder(uploadsInFolder.map(u => u.id)).catch(() => {});
    }

    newFolders.forEach(f => {
      if (removedFolderId === parseInt(f.parentId!)) {
        f.parentId = "";
        DSAApi.removeFolderFromFolder(f.id!).catch(() => {});
      }
    });

    setFolders([...newFolders]);
    if (activeFolder?.id === removedFolderId) clearFolder();
    ensureFolderCounts(uploads, [...newFolders]);
    setFolderToMove(null);
  }

  const CreateFolderButton = (
    <Button
      className={"create-fld-btn secondary"}
      onClick={() => { setFolderToMove(null); setSelectedUpload(null); setCreateFolderModal(true); }}
    >
      <PlusOutlined /> New Folder
    </Button>
  );

  const uploadIds = selectedUpload ? [selectedUpload.id] : [];
  const visibleUploads = uploads.filter(u => u.visible);

  return <div>
    {loading && <div className={"dsa"}>
      <div className={"spinner"}><Spinner /></div>
    </div>}

    {!loading && <section className={"dsau"}>
      <header>
        {mobile && <Input type="search" prefix={<SearchOutlined />} placeholder="Search Uploads" value={search}
          onChange={ev => onSearchChange(ev.target.value)} />}

        {activeFolder
          ? <div className={"folder-breadcrumb"}>
              <span className={"lk"} onClick={() => clearFolder()}>My Uploads</span>
              {breadcrumbs.map((bc, i) => (
                <span key={i}>
                  <span className={"rt-arw"}><RightOutlined /></span>
                  <span className={"lk"} onClick={() => onFolderClick(bc)}>{bc.name}</span>
                </span>
              ))}
              <span>
                <span className={"rt-arw"}><RightOutlined /></span>
                <b>{activeFolder.name}</b>
              </span>
            </div>
          : <h3>My Uploads</h3>
        }

        <div style={{ display: "flex", gap: 8 }}>
          {(visibleUploads.length > 0 || activeFolder) &&
            <UploadDisplayImage
              className="ant-btn grey"
              beforeUpload={() => setLoading(true)}
              afterUpload={() => setLoading(false)}
              uploads={uploads}
              setUploads={setUploads}
              folderId={activeFolder?.id}
              folders={folders}
              ensureFolderCounts={ensureFolderCounts}
            />
          }
          {CreateFolderButton}
        </div>

        {!mobile && <Input type="search" prefix={<SearchOutlined />} placeholder="Search Uploads" value={search}
          onChange={ev => onSearchChange(ev.target.value)} />}
      </header>

      {(mobile || activeFolder || folders.length !== 0) &&
        <section className={"folders"}>
          {!activeFolder && folders.length > 0 && <>
            <h4>Folders</h4>
            {!isFoldersExpanded
              ? <span className={"fld-collapse-btn"} onClick={() => setIsFoldersExpanded(true)}><DownOutlined /></span>
              : <span className={"fld-collapse-btn"} onClick={() => setIsFoldersExpanded(false)}><UpOutlined /></span>
            }
          </>}

          {folders.length !== 0 &&
            <ul className={`folders-list ${!isFoldersExpanded ? "collapsed" : ""}`}>
              {shownFolders.map(it => (
                <li key={it.id}>
                  <DsaFolderButton
                    folder={it}
                    active={it.id === activeFolder?.id}
                    onClick={onFolderClick}
                    deleteFolder={onDeleteFolderClick}
                    moveToFolder={onMoveFolderToFolderClick}
                    folderThumbs={folderThumbs}
                    folderNameExists={folderNameExists}
                    afterFolderRename={afterFolderRename}
                    isMobile={mobile}
                  />
                </li>
              ))}
            </ul>}
        </section>}

      {activeFolder && visibleUploads.length === 0 && shownFolders.length === 0 && !search &&
        <div className={"empty-folder-placeholder"}>
          <FolderOutlined />
          <h3>This folder is currently empty</h3>
          <div className={"create-desc"}>Upload an image or move an existing upload here.</div>
        </div>}

      {(uploads.length === 0 || (visibleUploads.length === 0 && !activeFolder)) && !search && <div className={"no-data"}>
        <img src="https://timber.mhmcdn.com/site/marketing/icons/Upload.svg" alt="Upload" />
        <p>You don't have any uploads yet.</p>
        <div className={"upload-btn-wrapper"}>
          <UploadDisplayImage
            beforeUpload={() => setLoading(true)}
            afterUpload={() => setLoading(false)}
            uploads={uploads}
            setUploads={setUploads}
            folderId={activeFolder?.id}
            folders={folders}
            ensureFolderCounts={ensureFolderCounts}
          />
        </div>
      </div>}

      {search && visibleUploads.length === 0 && <div className="no-matches">No matches found.</div>}

      {visibleUploads.length > 0 && <ul className="uploads">
        {visibleUploads.map(it => <UploadCard
          key={it.id}
          it={it}
          setDeleteModal={setDeleteModal}
          setSelected={setSelected}
          setPreviewModal={setPreviewModal}
          uploads={uploads}
          setUploads={setUploads}
          onMoveToFolder={onMoveUploadToFolder}
          onRemoveFromFolder={onRemoveUploadFromFolder}
          inFolder={!!activeFolder}
        />)}
      </ul>}
    </section>}

    <DeleteUploadModal
      upload={selected}
      isVisible={isDeleteModal}
      onClose={onDeleteModalClose}
    />

    <PreviewModal
      previewType="upload"
      previewUploadId={selected.id}
      isVisible={isPreviewModal}
      onClose={() => setPreviewModal(false)}
    />

    {!mobile
      ? <DsaCreateFolderModal
          uploadIds={uploadIds}
          isVisible={isCreateFolderModal}
          onClose={onCreateFolderModalClose}
          folderToMove={folderToMove}
          folderNameExists={folderNameExists}
          activeFolder={activeFolder}
        />
      : <DsaCreateFolderDrawer
          uploadIds={uploadIds}
          isVisible={isCreateFolderModal}
          onClose={onCreateFolderModalClose}
          folderToMove={folderToMove}
          folderNameExists={folderNameExists}
          activeFolder={activeFolder}
        />
    }

    {!mobile
      ? <DsaMoveToFolderModal
          isVisible={isMoveToFolderModal}
          uploadIds={uploadIds}
          folders={folders}
          activeFolder={activeFolder}
          onCloseMovedToFolder={onMoveToFolderClose}
          onCloseMovedToUploads={onMoveToUploadsClose}
          onCloseMovedFolderToFolder={onMoveToFolderMovedFolderClose}
          folderToMove={folderToMove}
          folderThumbs={folderThumbs}
          folderNameExists={folderNameExists}
          onCreateFolderModalClose={onCreateFolderModalClose}
        />
      : <DsaMoveToFolderDrawer
          isVisible={isMoveToFolderModal}
          uploadIds={uploadIds}
          folders={folders}
          activeFolder={activeFolder}
          onBack={() => setMoveToFolderModal(false)}
          onCloseMovedToFolder={onMoveToFolderClose}
          onCloseMovedToUploads={onMoveToUploadsClose}
          onCloseMovedFolderToFolder={onMoveToFolderMovedFolderClose}
          folderToMove={folderToMove}
          folderThumbs={folderThumbs}
          folderNameExists={folderNameExists}
          onCreateFolderModalClose={onCreateFolderModalClose}
        />
    }

    <DsaDeleteFolderModal
      folder={folderToMove}
      isVisible={isDeleteFolderModal}
      onClose={onDeleteFolderSuccess}
    />
  </div>;
}
